// ============================================================
// FILE: Home.js
// LOCATION: C:\gardenmarket\web\src\pages\public\Home.js
// ============================================================
// PURPOSE:
//   Two-audience landing page for GardenHand.
//   Non-logged-in visitors see a full marketing page.
//   Logged-in users are redirected to their dashboard via
//   HomeRoute in App.js (this page only shows to guests).
// ============================================================

import { Link } from 'react-router-dom';

const BUYER_STEPS = [
    { n:'1', icon:'📋', title:'Post your job',     desc:'Describe what you need, set your budget, and upload photos. Free for buyers — no subscription.' },
    { n:'2', icon:'💬', title:'Receive quotes',    desc:'Local gardeners send you quotes with their price and availability. Usually within hours.' },
    { n:'3', icon:'✅', title:'Choose and book',   desc:'Compare gardeners by rating, price, and experience. Accept the quote that works for you.' },
    { n:'4', icon:'⭐', title:'Review',             desc:'After the job, leave a review to help others find great gardeners in their area.' },
];

const GARDENER_BENEFITS = [
    { icon:'🎁', title:'Free to join, $10 to start',  desc:'No signup cost, no subscription. New gardeners get $10 welcome credit — that covers your first few jobs before you top up.' },
    { icon:'💰', title:'Pay only when you win',       desc:'Quoting is always free. A fee of $1–$10 comes off your credit only when a customer accepts your quote.' },
    { icon:'🎯', title:'Jobs come to you',            desc:'We match you with jobs in your service area that fit your skills. No cold calling.' },
    { icon:'📅', title:'You set your schedule',       desc:'Accept the jobs that suit you. Work as much or as little as you want.' },
];

// Stats strip.
//
// IMPORTANT: every figure here must be verifiable. The previous
// version showed "⭐ 5.0 Average gardener rating" as a hardcoded
// value with no completed jobs behind it — a fabricated rating
// claim is a real legal exposure under Australian Consumer Law
// (misleading representations about testimonials/ratings), and
// a 5.0 reads as fake to visitors anyway.
//
// Once there are enough real reviews (say 10+), wire a live
// average in from the reviews table and add it back. Until then
// these four are all statements of fact about how the service
// works, not performance claims.
const STATS = [
    { n:'Free',    l:'To join and to quote' },
    { n:'$10',     l:'Welcome credit for new gardeners' },
    { n:'$1–$10',  l:'Fee per job won — nothing until then' },
    { n:'0',       l:'Lead fees, subscriptions, lock-in' },
];

export default function Home() {
    return (
        <div style={styles.page}>

            {/* ── Hero ─────────────────────────────────────── */}
            <div style={styles.hero}>
                <div style={styles.heroInner}>
                    <div style={styles.heroBrand}>🌿 GardenHand</div>
                    <h1 style={styles.heroTitle}>Your local garden crew</h1>
                    <p style={styles.heroSub}>
                        Connect with trusted local gardeners — or find gardening work near you.
                    </p>
                    <div style={styles.heroBtns}>
                        <Link to="/register?type=buyer" style={styles.btnPrimary}>
                            🌱 Post a Job — It's Free
                        </Link>
                        <Link to="/register?type=gardener" style={styles.btnOutline}>
                            🛠 Join as a Gardener
                        </Link>
                    </div>
                    <p style={styles.heroNote}>
                        Gardeners join free and start with $10 credit — enough for your first few jobs
                    </p>
                </div>
            </div>

            {/* ── Stats strip ──────────────────────────────── */}
            <div className="gh-grid-4" style={styles.statsStrip}>
                {STATS.map(s => (
                    <div key={s.l} style={styles.statItem}>
                        <div style={styles.statNum}>{s.n}</div>
                        <div style={styles.statLabel}>{s.l}</div>
                    </div>
                ))}
            </div>

            {/* ── How it works (buyers) ────────────────────── */}
            <div style={styles.section}>
                <div style={styles.sectionInner}>
                    <div style={styles.audienceTag}>For homeowners &amp; businesses</div>
                    <h2 style={styles.sectionTitle}>Get your garden sorted in 4 steps</h2>
                    <p style={styles.sectionSub}>Post a job for free and receive quotes from local gardeners — no subscription, no hassle.</p>
                    <div style={styles.stepsGrid}>
                        {BUYER_STEPS.map(s => (
                            <div key={s.n} style={styles.stepCard}>
                                <div style={styles.stepIcon}>{s.icon}</div>
                                <div style={styles.stepNum}>{s.n}</div>
                                <h3 style={styles.stepTitle}>{s.title}</h3>
                                <p style={styles.stepDesc}>{s.desc}</p>
                            </div>
                        ))}
                    </div>
                    <div style={styles.ctaRow}>
                        <Link to="/register?type=buyer" style={styles.btnGreen}>
                            Post a Job — It's Free →
                        </Link>
                        <Link to="/login" style={styles.btnLink}>Already have an account? Sign in</Link>
                    </div>
                </div>
            </div>

            {/* ── Divider ──────────────────────────────────── */}
            <div style={styles.divider} />

            {/* ── For gardeners ────────────────────────────── */}
            <div style={{ ...styles.section, backgroundColor:'#F0FDF4' }}>
                <div style={styles.sectionInner}>
                    <div style={{ ...styles.audienceTag, backgroundColor:'#DCFCE7', color:'#14532D' }}>For gardeners &amp; landscapers</div>
                    <h2 style={styles.sectionTitle}>Grow your gardening business</h2>
                    <p style={styles.sectionSub}>Find work in your local area. Solo operators, teams, and franchise businesses all welcome.</p>
                    <div style={styles.benefitsGrid}>
                        {GARDENER_BENEFITS.map(b => (
                            <div key={b.title} style={styles.benefitCard}>
                                <div style={styles.benefitIcon}>{b.icon}</div>
                                <h3 style={styles.benefitTitle}>{b.title}</h3>
                                <p style={styles.benefitDesc}>{b.desc}</p>
                            </div>
                        ))}
                    </div>

                    {/* Pricing table */}
                    <div style={styles.pricingBox}>
                        <h3 style={styles.pricingTitle}>Simple, fair pricing</h3>
                        <p style={styles.pricingNote}>Pay only when you win a job. No monthly fees, no lock-in.</p>
                        <div className="gh-grid-4" style={styles.pricingGrid}>
                            {[
                                { range:'Job up to $25',    fee:'$1' },
                                { range:'$26 – $100',       fee:'$2' },
                                { range:'$101 – $500',      fee:'$5' },
                                { range:'$501 and above',   fee:'$10' },
                            ].map(p => (
                                <div key={p.range} style={styles.pricingItem}>
                                    <span style={styles.pricingRange}>{p.range}</span>
                                    <span style={styles.pricingFee}>{p.fee}</span>
                                </div>
                            ))}
                        </div>
                        <div style={styles.creditCallout}>
                            <strong style={styles.creditCalloutTitle}>🎁 New gardeners start with $10 credit</strong>
                            <span style={styles.creditCalloutText}>
                                That's your first ten small jobs, five mid-size jobs, or one large one — free.
                                Top up in $10, $20, $50 or $100 packs whenever you're ready.
                            </span>
                        </div>
                        <p style={styles.pricingFootnote}>
                            Prices shown in local currency. Your balance can go to -$5 before topping up is required.
                        </p>
                    </div>

                    <div style={styles.ctaRow}>
                        <Link to="/register?type=gardener" style={styles.btnGreen}>
                            Join as a Gardener →
                        </Link>
                        <Link to="/login" style={styles.btnLink}>Already have an account? Sign in</Link>
                    </div>
                </div>
            </div>

            {/* ── Footer ───────────────────────────────────── */}
            <div style={styles.footer}>
                <div style={styles.footerInner}>
                    <span style={styles.footerBrand}>🌿 GardenHand</span>
                    <span style={styles.footerTagline}>Your local garden crew</span>
                    <span style={styles.footerCopy}>© {new Date().getFullYear()} GardenHand · gardenhand.online</span>
                </div>
            </div>
        </div>
    );
}

const GREEN       = '#2d6a4f';
const GREEN_DARK  = '#1B4332';
const GREEN_LIGHT = '#DCFCE7';

const styles = {
    page:           { fontFamily:'system-ui, Arial, sans-serif' },

    // Hero
    hero:           { background:`linear-gradient(135deg, ${GREEN_DARK} 0%, ${GREEN} 60%, #52b788 100%)`, color:'white', padding:'0' },
    heroInner:      { maxWidth:'800px', margin:'0 auto', padding:'90px 24px 80px', textAlign:'center' },
    heroBrand:      { fontSize:'0.9rem', fontWeight:700, letterSpacing:'0.12em', opacity:0.75, marginBottom:'20px', textTransform:'uppercase' },
    heroTitle:      { fontSize:'3rem', margin:'0 0 16px', fontWeight:800, lineHeight:1.1 },
    heroSub:        { fontSize:'1.2rem', margin:'0 0 36px', opacity:0.9, lineHeight:1.5 },
    heroBtns:       { display:'flex', gap:'14px', justifyContent:'center', flexWrap:'wrap', marginBottom:'20px' },
    btnPrimary:     { display:'inline-block', backgroundColor:'white', color:GREEN_DARK, padding:'14px 28px', borderRadius:'8px', textDecoration:'none', fontWeight:700, fontSize:'1rem' },
    btnOutline:     { display:'inline-block', backgroundColor:'transparent', color:'white', padding:'14px 28px', borderRadius:'8px', textDecoration:'none', fontWeight:700, fontSize:'1rem', border:'2px solid rgba(255,255,255,0.7)' },
    heroNote:       { fontSize:'0.85rem', opacity:0.65 },

    // Stats
    // grid columns live in responsive.css (.gh-grid-4) so they collapse
    // to 2-up on mobile — inline gridTemplateColumns cannot be overridden
    // by a media query.
    statsStrip:     { backgroundColor:GREEN_DARK, color:'white', padding:'24px', textAlign:'center' },
    statItem:       { padding:'8px' },
    statNum:        { fontSize:'1.6rem', fontWeight:800, color:'#52b788' },
    statLabel:      { fontSize:'0.8rem', opacity:0.75, marginTop:'4px' },

    // Sections
    section:        { padding:'70px 24px' },
    sectionInner:   { maxWidth:'960px', margin:'0 auto' },
    audienceTag:    { display:'inline-block', backgroundColor:'#E0F2FE', color:'#0369A1', fontSize:'0.78rem', fontWeight:700, padding:'4px 12px', borderRadius:'999px', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'16px' },
    sectionTitle:   { fontSize:'2rem', fontWeight:800, color:GREEN_DARK, marginBottom:'12px', textAlign:'center' },
    sectionSub:     { color:'#4B5563', fontSize:'1.05rem', textAlign:'center', marginBottom:'48px', maxWidth:'600px', marginLeft:'auto', marginRight:'auto', lineHeight:1.6 },

    // Buyer steps
    stepsGrid:      { display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px,1fr))', gap:'20px', marginBottom:'40px' },
    stepCard:       { textAlign:'center', padding:'28px 20px', borderRadius:'12px', background:'white', boxShadow:'0 2px 8px rgba(0,0,0,0.07)', border:'1px solid #E5E7EB', position:'relative' },
    stepIcon:       { fontSize:'2rem', marginBottom:'8px' },
    stepNum:        { position:'absolute', top:'12px', right:'12px', width:'24px', height:'24px', borderRadius:'50%', backgroundColor:GREEN_LIGHT, color:GREEN, fontSize:'0.75rem', fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center' },
    stepTitle:      { fontSize:'1rem', fontWeight:700, color:GREEN_DARK, marginBottom:'8px' },
    stepDesc:       { color:'#6B7280', fontSize:'0.88rem', lineHeight:1.5 },

    // CTA row
    ctaRow:         { display:'flex', gap:'16px', alignItems:'center', flexWrap:'wrap', justifyContent:'center', marginTop:'8px' },
    btnGreen:       { display:'inline-block', backgroundColor:GREEN, color:'white', padding:'13px 28px', borderRadius:'8px', textDecoration:'none', fontWeight:700, fontSize:'1rem' },
    btnLink:        { color:GREEN, fontWeight:600, textDecoration:'none', fontSize:'0.9rem' },

    // Divider
    divider:        { height:'1px', backgroundColor:'#E5E7EB', margin:'0 24px' },

    // Gardener benefits
    benefitsGrid:   { display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px,1fr))', gap:'20px', marginBottom:'40px' },
    benefitCard:    { padding:'24px', borderRadius:'12px', background:'white', boxShadow:'0 2px 8px rgba(0,0,0,0.06)', border:'1px solid #D1FAE5' },
    benefitIcon:    { fontSize:'2rem', marginBottom:'12px' },
    benefitTitle:   { fontSize:'1rem', fontWeight:700, color:GREEN_DARK, marginBottom:'8px' },
    benefitDesc:    { color:'#4B5563', fontSize:'0.88rem', lineHeight:1.5 },

    // Pricing
    pricingBox:     { backgroundColor:'white', borderRadius:'12px', padding:'28px 32px', border:'1px solid #D1FAE5', boxShadow:'0 2px 8px rgba(0,0,0,0.06)', marginBottom:'36px' },
    pricingTitle:   { fontSize:'1.1rem', fontWeight:700, color:GREEN_DARK, marginBottom:'6px' },
    pricingNote:    { color:'#6B7280', fontSize:'0.88rem', marginBottom:'16px' },
    pricingGrid:    { marginBottom:'14px' },
    pricingItem:    { textAlign:'center', padding:'12px 8px', backgroundColor:'#F0FDF4', borderRadius:'8px' },
    pricingRange:   { display:'block', fontSize:'0.75rem', color:'#6B7280', marginBottom:'4px' },
    pricingFee:     { display:'block', fontSize:'1.2rem', fontWeight:800, color:GREEN },
    creditCallout:  { display:'block', backgroundColor:GREEN_LIGHT, borderRadius:'8px', padding:'14px 16px', marginBottom:'14px' },
    creditCalloutTitle:{ display:'block', color:GREEN_DARK, fontSize:'0.95rem', marginBottom:'4px' },
    creditCalloutText:{ display:'block', color:'#14532D', fontSize:'0.85rem', lineHeight:1.5 },
    pricingFootnote:{ fontSize:'0.75rem', color:'#9CA3AF' },

    // Footer
    footer:         { backgroundColor:GREEN_DARK, color:'white', padding:'24px' },
    footerInner:    { maxWidth:'960px', margin:'0 auto', display:'flex', gap:'16px', alignItems:'center', flexWrap:'wrap', justifyContent:'space-between' },
    footerBrand:    { fontWeight:800, fontSize:'1rem' },
    footerTagline:  { color:'#86EFAC', fontSize:'0.88rem' },
    footerCopy:     { fontSize:'0.78rem', opacity:0.5 },
};
